import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePasswordReset, useValidatePasswordResetToken } from '../../hooks/api/use-authentication.hook';
import { ResetPasswordForm } from './ResetPasswordForm';
import { LoadingIndicator } from '../LoadingIndicator';

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token') ?? '';

  const { error: validateError, loading: validateLoading } = useValidatePasswordResetToken({ token });
  const [passwordReset, { error, loading }] = usePasswordReset();

  const {
    register,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm<ResetPasswordForm>({ resolver: classValidatorResolver(ResetPasswordForm) });

  const onSubmit: SubmitHandler<ResetPasswordForm> = async ({ newPassword, confirmPassword }) => {
    if (newPassword !== confirmPassword) {
      setError('confirmPassword', { message: 'Password does not match' });
      return;
    }

    await passwordReset({ token, newPassword });
  };

  const renderContent = () => {
    if (error || validateError) {
      return <Alert severity="error">{(error || validateError)?.response?.data.message}</Alert>;
    } else if (isSubmitSuccessful) {
      return <Alert severity="success">Password changed successfully!</Alert>;
    } else if (validateLoading) {
      return <LoadingIndicator />;
    }

    return (
      <>
        <TextField
          {...register('newPassword')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={loading}
          error={Boolean(errors.newPassword)}
          helperText={errors.newPassword?.message}
          label="New Password"
          margin="dense"
          size="small"
          type="password"
          fullWidth
        />
        <TextField
          {...register('confirmPassword')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={loading}
          error={Boolean(errors.confirmPassword)}
          helperText={errors.confirmPassword?.message}
          label="Confirm Password"
          margin="dense"
          size="small"
          type="password"
          fullWidth
        />
      </>
    );
  };

  return (
    <>
      <Dialog open={true}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>Reset password</DialogTitle>
          <DialogContent>{renderContent()}</DialogContent>
          <DialogActions>
            <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
              {isSubmitSuccessful ? 'Close' : 'Cancel'}
            </Button>
            {!isSubmitSuccessful ? (
              <LoadingButton color="primary" type="submit" variant="contained" loading={isSubmitting}>
                Reset Password
              </LoadingButton>
            ) : null}
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};
